import { useCallback, useMemo } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import {
	useUpdatePersonalizedPetitionTask,
	UPDATE_TASK_ERROR_MAP,
} from 'src/app/pages/starterDashboardV2/shared/hooks';

import { TASKS_CONFIGS } from '../../../../constants';
import { useCompleteTaskByCheckbox } from '../../../../hooks/useNextSteps/useTasks/useCompleteTaskByCheckbox';
import type { Task } from '../../../../types';

type ExistingTask = Readonly<{ id: Task['id']; name: Task['name']; status: Task['status'] }>;

type CheckboxAction = () => Promise<void>;

export function useDynamicCheckboxAction(): (featureKey: string | null, taskId: string) => CheckboxAction {
	const {
		data,
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const getCheckboxHandler = useCompleteTaskByCheckbox();

	const navigate = useNavigate();
	const { pathname, search } = useLocation();

	const { mutate: updateTask } = useUpdatePersonalizedPetitionTask({
		onSuccess: () => {
			refreshData();
		},
		onError: (error) => {
			if (error === UPDATE_TASK_ERROR_MAP.UNAUTHENTICATED_ERROR.code) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join?redirectTo=${params}`);
			}
		},
	});

	const existingTasksByName = useMemo<ReadonlyMap<string, ExistingTask>>(() => {
		const { activeTasks } = data.petition;
		if (activeTasks.__typename !== 'PetitionTasksConnection') return new Map();
		return new Map(activeTasks.nodes.map((task) => [task.name, task]));
	}, [data.petition]);

	const petitionId = data.petition.id;

	return useCallback(
		(featureKey: string | null, taskId: string) => {
			if (featureKey && Object.hasOwn(TASKS_CONFIGS, featureKey)) {
				const existingTask = existingTasksByName.get(featureKey);
				if (existingTask) {
					return getCheckboxHandler(existingTask.id, existingTask.name, existingTask.status);
				}
			}
			return async () => {
				await updateTask({ petitionId, personalizedPetitionTaskId: taskId, status: 'COMPLETED' });
			};
		},
		[getCheckboxHandler, existingTasksByName, petitionId, updateTask],
	);
}
